// dashboard/components/YourCoursesCards.jsx
import React from 'react';
import { Box, Chip, IconButton, LinearProgress, Typography } from '@mui/material';
import { AccessTime, Lock, MoreHoriz, ShowChart, BarChart, Timeline, TrendingUp, AttachMoney } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const CourseCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'courseStatus',
})(({ courseStatus }) => {
  const paused = courseStatus === 'Paused';
  const finished = courseStatus === 'Finished';
  const accent = paused ? '#ff951c' : finished ? '#112d5b' : '#075ccc';

  return {
    minHeight: 168,
    padding: '26px 26px 24px',
    borderRadius: 14,
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    background: '#eef2f7',
    color: '#062c60',
    transition: 'transform .2s ease, box-shadow .2s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 12px 25px rgba(7, 49, 106, .12)',
      // Trigger the icon animation on card hover
      '& .forex-icon': {
        '& svg': {
          transform: 'rotate(360deg) scale(1.4)',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          color: '#075ccc',
        },
      },
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: 'transform 0.1s ease',
    },
    '& .course-status': {
      height: 29,
      borderRadius: 4,
      fontSize: 14,
      fontWeight: 500,
      color: paused ? '#ff8514' : '#fff',
      backgroundColor: paused ? '#fff7ed' : accent,
    },
    '& .course-action': {
      width: 32,
      height: 29,
      borderRadius: 4,
      color: paused ? '#ff8514' : '#fff',
      backgroundColor: paused ? '#fff7ed' : accent,
      '&:hover': { backgroundColor: paused ? '#ffefdc' : accent },
    },
  };
});

// Forex Illustration Icon with spin and scale on card hover
const ForexIllustration = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: -2,
  bottom: -12,
  color: '#15498e',
  opacity: 0.98,
  cursor: 'pointer',
  zIndex: 0,
  '& svg': { 
    fontSize: 80,
    transform: 'rotate(-8deg)',
    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

// Get Forex icon based on course name
const getForexIcon = (courseName) => {
  if (!courseName) return <ShowChart />;
  if (courseName.includes('EUR/USD')) return <ShowChart />;
  if (courseName.includes('GBP/USD')) return <BarChart />;
  if (courseName.includes('USD/JPY')) return <Timeline />;
  if (courseName.includes('AUD/USD')) return <TrendingUp />;
  if (courseName.includes('Forex')) return <AttachMoney />;
  return <ShowChart />;
};

export default function YourCoursesCards({ course, onClick }) {
  // Check if course exists, if not use a default
  if (!course) {
    return null;
  }

  const paused = course.status === 'Paused';
  const finished = course.status === 'Finished';
  const forexIcon = getForexIcon(course.name);

  return (
    <CourseCard courseStatus={course.status} onClick={() => onClick && onClick(course)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
        <Chip label={course.status} className="course-status" />
        {!finished && (
          <IconButton className="course-action" size="small" aria-label="Course access">
            {paused ? <Lock sx={{ fontSize: 16 }} /> : <Lock sx={{ fontSize: 17 }} />}
          </IconButton>
        )}
        {course.name && course.name.includes('EUR/USD') && (
          <IconButton className="course-action" size="small" aria-label="Course time">
            <AccessTime sx={{ fontSize: 17 }} />
          </IconButton>
        )}
        <IconButton
          size="small"
          aria-label="More course options"
          onClick={(event) => event.stopPropagation()}
          sx={{ ml: 'auto', mt: -0.5, mr: 1.5, color: '#1c569c' }}
        >
          <MoreHoriz />
        </IconButton>
      </Box>

      <Typography sx={{ mt: 3.5, fontSize: 22, lineHeight: 1.2, fontWeight: 500, position: 'relative', zIndex: 1 }}>
        {course.name || 'Course'}
      </Typography>
      <Typography sx={{ mt: 1, color: '#b1b8c4', fontSize: 14, position: 'relative', zIndex: 1 }}>
        {finished ? 'Completed 🎉' : `${course.daysLeft || 0} Days Left`}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={course.progress || 0}
        sx={{
          mt: 2,
          height: 3,
          borderRadius: 2,
          width: '72%',
          bgcolor: 'rgba(12, 55, 112, .10)',
          position: 'relative',
          zIndex: 1,
          '& .MuiLinearProgress-bar': {
            bgcolor: paused ? '#ff951c' : '#075ccc',
            transition: 'width 1s ease',
          },
        }}
      />
      
      {/* Forex Illustration with spin and scale on card hover */}
      <ForexIllustration className="forex-icon" status={course.status}>
        {forexIcon}
      </ForexIllustration>
    </CourseCard>
  );
}